/**
 * Slot Machine Skin Editor
 *
 * Tabbed interface for editing a single slot machine skin.
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Save,
  Loader2,
  Settings,
  Palette,
  Image,
  Layers,
  PartyPopper,
  Timer,
  Coins,
  Gamepad2,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';

// Tab components
import GeneralTab from './components/GeneralTab';
import StyleTab from './components/StyleTab';
import AssetsTab from './components/AssetsTab';
import SymbolsTab from './components/SymbolsTab';
import CelebrationsTab from './components/CelebrationsTab';
import AnimationsTab from './components/AnimationsTab';
import BettingTab from './components/BettingTab';
import GameOptionsTab from './components/GameOptionsTab';

import type {
  SlotMachineSkin,
  SkinStyleConfig,
  SkinAnimationConfig,
  SkinBetConfig,
  CelebrationConfig,
  GameOptionsConfig,
  SlotMachineSkinAsset,
  SlotMachineSkinSymbol,
} from '../../public/deals/components/slot-machine/themes/skin-types';

// =============================================================================
// Types
// =============================================================================

type TabId = 'general' | 'style' | 'assets' | 'symbols' | 'celebrations' | 'animations' | 'betting' | 'game-options';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ComponentType<{ size?: number }>;
}

interface SkinEditorState {
  name: string;
  slug: string;
  description: string;
  status: 'draft' | 'published' | 'archived';
  isDefault: boolean;
  styleConfig: SkinStyleConfig;
  animationConfig: SkinAnimationConfig;
  betConfig: SkinBetConfig;
  celebrationConfig: CelebrationConfig;
  gameOptions: GameOptionsConfig;
}

// =============================================================================
// Tabs Configuration
// =============================================================================

const TABS: Tab[] = [
  { id: 'general', label: 'General', icon: Settings },
  { id: 'style', label: 'Style', icon: Palette },
  { id: 'assets', label: 'Assets', icon: Image },
  { id: 'symbols', label: 'Symbols', icon: Layers },
  { id: 'celebrations', label: 'Celebrations', icon: PartyPopper },
  { id: 'animations', label: 'Animations', icon: Timer },
  { id: 'betting', label: 'Betting', icon: Coins },
  { id: 'game-options', label: 'Game Options', icon: Gamepad2 },
];

// =============================================================================
// Default Values
// =============================================================================

const DEFAULT_STYLE_CONFIG: SkinStyleConfig = {
  primary: '#FFD700',
  secondary: '#1E3A5F',
  accent: '#FF6B35',
  background: '#0a1628',
  winGlow: '#FFD700',
  reelBackground: '#1a2d4a',
  textPrimary: '#FFFFFF',
  textSecondary: '#94a3b8',
  winText: '#FFD700',
  fontFamily: 'Inter, sans-serif',
  fontFamilyNumbers: 'JetBrains Mono, monospace',
};

const DEFAULT_ANIMATION_CONFIG: SkinAnimationConfig = {
  spinDuration: 2500,
  reelStopDelay: 200,
  reelEasing: 'easeOutBack',
  reelBounceIntensity: 0.15,
  anticipation: true,
  anticipationDuration: 150,
};

const DEFAULT_BET_CONFIG: SkinBetConfig = {
  minLines: 1,
  maxLines: 20,
  betOptions: [1, 2, 3, 5, 10],
  defaultBet: 1,
  defaultLines: 20,
};

const DEFAULT_CELEBRATION_CONFIG: CelebrationConfig = {
  bigWinThreshold: 10,
  megaWinThreshold: 25,
  bigWinLabel: 'BIG WIN!',
  megaWinLabel: 'MEGA WIN!',
  jackpotLabel: 'JACKPOT!',
  winAmountFont: 'Inter, sans-serif',
  winLabelFont: 'Inter, sans-serif',
  bigWinColor: '#22c55e',
  megaWinColor: '#eab308',
  jackpotColor: '#f97316',
  bigWinParticles: ['#22c55e', '#4ade80', '#86efac'],
  megaWinParticles: ['#eab308', '#facc15', '#fde047'],
  jackpotParticles: ['#f97316', '#fb923c', '#fdba74'],
  countUpDuration: 2000,
  displayDuration: 3000,
};

const DEFAULT_GAME_OPTIONS: GameOptionsConfig = {
  layout: 'classic',
  reelCount: 5,
  rowCount: 3,
  paylineType: 'fixed',
  paylineCount: 20,
  wildFeatures: {
    expanding: false,
    sticky: false,
    walking: false,
    multiplier: false,
  },
  scatterFeatures: {
    paysAnywhere: true,
    triggersFeature: 'free_spins',
    minForTrigger: 3,
  },
  bonusGameType: 'free_spins',
  freeSpins: {
    defaultCount: 10,
    multiplier: 1,
    retriggerable: true,
  },
  features: {
    cascading: false,
    megaways: false,
    buyBonus: false,
    gamble: false,
    autoplay: true,
  },
};

// =============================================================================
// Component
// =============================================================================

export default function SkinEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNewSkin = id === 'new';

  const [activeTab, setActiveTab] = useState<TabId>('general');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Form state
  const [formState, setFormState] = useState<SkinEditorState>({
    name: '',
    slug: '',
    description: '',
    status: 'draft',
    isDefault: false,
    styleConfig: DEFAULT_STYLE_CONFIG,
    animationConfig: DEFAULT_ANIMATION_CONFIG,
    betConfig: DEFAULT_BET_CONFIG,
    celebrationConfig: DEFAULT_CELEBRATION_CONFIG,
    gameOptions: DEFAULT_GAME_OPTIONS,
  });

  // =============================================================================
  // Queries
  // =============================================================================

  const { data: skinData, isLoading: skinLoading } = useQuery({
    queryKey: ['admin-slot-skin', id],
    queryFn: async () => {
      if (isNewSkin) return null;
      const response = await fetch(`/api/slot-skins/${id}/full`);
      if (!response.ok) throw new Error('Failed to fetch skin');
      return response.json() as Promise<{
        skin: SlotMachineSkin;
        assets: SlotMachineSkinAsset[];
        symbols: SlotMachineSkinSymbol[];
      }>;
    },
    enabled: !isNewSkin,
  });

  const { data: assetsData, isLoading: assetsLoading } = useQuery({
    queryKey: ['admin-slot-skin-assets', id],
    queryFn: async () => {
      if (isNewSkin) return { assets: [] };
      const response = await fetch(`/api/slot-skins/${id}/assets`);
      if (!response.ok) throw new Error('Failed to fetch assets');
      return response.json() as Promise<{ assets: SlotMachineSkinAsset[] }>;
    },
    enabled: !isNewSkin,
  });

  const { data: symbolsData, isLoading: symbolsLoading } = useQuery({
    queryKey: ['admin-slot-skin-symbols', id],
    queryFn: async () => {
      if (isNewSkin) return { symbols: [] };
      const response = await fetch(`/api/slot-skins/${id}/symbols`);
      if (!response.ok) throw new Error('Failed to fetch symbols');
      return response.json() as Promise<{ symbols: SlotMachineSkinSymbol[] }>;
    },
    enabled: !isNewSkin,
  });

  // =============================================================================
  // Initialize form from fetched data
  // =============================================================================

  // Track if form has been initialized to prevent re-initialization
  const initializedRef = useRef(false);

  // Initialize form state from fetched data (only once per skin)
  // Using queueMicrotask to schedule state update asynchronously to avoid cascading renders
  useEffect(() => {
    if (skinData?.skin && !initializedRef.current) {
      initializedRef.current = true;
      const skin = skinData.skin;
      // Using type assertion since celebrationConfig and gameOptions are optional on the skin
      const skinWithConfigs = skin as SlotMachineSkin & {
        celebrationConfig?: CelebrationConfig;
        gameOptions?: GameOptionsConfig;
      };
      queueMicrotask(() => {
        setFormState({
          name: skinWithConfigs.name,
          slug: skinWithConfigs.slug,
          description: skinWithConfigs.description || '',
          status: skinWithConfigs.status,
          isDefault: skinWithConfigs.isDefault,
          styleConfig: { ...DEFAULT_STYLE_CONFIG, ...skinWithConfigs.styleConfig },
          animationConfig: { ...DEFAULT_ANIMATION_CONFIG, ...skinWithConfigs.animationConfig },
          betConfig: { ...DEFAULT_BET_CONFIG, ...skinWithConfigs.betConfig },
          celebrationConfig: { ...DEFAULT_CELEBRATION_CONFIG, ...skinWithConfigs.celebrationConfig },
          gameOptions: { ...DEFAULT_GAME_OPTIONS, ...skinWithConfigs.gameOptions },
        });
      });
    }
  }, [skinData]);

  // =============================================================================
  // Mutations
  // =============================================================================

  const saveMutation = useMutation({
    mutationFn: async () => {
      const method = isNewSkin ? 'POST' : 'PUT';
      const url = isNewSkin ? '/api/slot-skins' : `/api/slot-skins/${id}`;

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formState.name,
          slug: formState.slug,
          description: formState.description,
          status: formState.status,
          style_config: formState.styleConfig,
          animation_config: formState.animationConfig,
          bet_config: formState.betConfig,
          celebration_config: formState.celebrationConfig,
          game_options: formState.gameOptions,
        }),
      });

      if (!response.ok) throw new Error('Failed to save skin');
      return response.json();
    },
    onSuccess: (data) => {
      setHasUnsavedChanges(false);
      setSaveStatus('saved');
      queryClient.invalidateQueries({ queryKey: ['admin-slot-skins'] });
      queryClient.invalidateQueries({ queryKey: ['admin-slot-skin', id] });

      // If new skin, navigate to edit page
      if (isNewSkin && data.skin?.id) {
        navigate(`/admin/slot-skins/${data.skin.id}`, { replace: true });
      }

      setTimeout(() => setSaveStatus('idle'), 2000);
    },
    onError: () => {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    },
  });

  const publishMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/slot-skins/${id}/publish`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to publish skin');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-slot-skin', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-slot-skins'] });
      setFormState((prev) => ({ ...prev, status: 'published' }));
    },
  });

  // =============================================================================
  // Handlers
  // =============================================================================

  const updateFormField = <K extends keyof SkinEditorState>(
    field: K,
    value: SkinEditorState[K]
  ) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handleSave = () => {
    setSaveStatus('saving');
    saveMutation.mutate();
  };

  // =============================================================================
  // Render
  // =============================================================================

  if (skinLoading && !isNewSkin) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/slot-skins')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isNewSkin ? 'Create New Skin' : formState.name || 'Edit Skin'}
            </h1>
            {!isNewSkin && (
              <p className="text-sm text-gray-500">
                /{formState.slug} &bull; {formState.status}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {saveStatus === 'saved' && (
            <span className="flex items-center gap-1 text-green-600 text-sm">
              <CheckCircle size={16} /> Saved
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="flex items-center gap-1 text-red-600 text-sm">
              <AlertCircle size={16} /> Save failed
            </span>
          )}
          {formState.status === 'draft' && !isNewSkin && (
            <button
              onClick={() => publishMutation.mutate()}
              disabled={publishMutation.isPending || hasUnsavedChanges}
              className="px-4 py-2 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 transition-colors disabled:opacity-50"
            >
              Publish
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saveMutation.isPending || !hasUnsavedChanges}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {saveMutation.isPending ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Save size={18} />
            )}
            Save Changes
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-1">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors
                  ${isActive
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {activeTab === 'general' && (
          <GeneralTab
            name={formState.name}
            slug={formState.slug}
            description={formState.description}
            status={formState.status}
            isDefault={formState.isDefault}
            onUpdate={(field, value) => {
              // Type-safe update for general fields
              updateFormField(field as keyof SkinEditorState, value as SkinEditorState[keyof SkinEditorState]);
            }}
          />
        )}
        {activeTab === 'style' && (
          <StyleTab
            config={formState.styleConfig}
            onUpdate={(config) => updateFormField('styleConfig', config)}
          />
        )}
        {activeTab === 'assets' && (
          <AssetsTab
            skinId={id || ''}
            assets={assetsData?.assets || []}
            isLoading={assetsLoading}
          />
        )}
        {activeTab === 'symbols' && (
          <SymbolsTab
            skinId={id || ''}
            symbols={symbolsData?.symbols || []}
            isLoading={symbolsLoading}
          />
        )}
        {activeTab === 'celebrations' && (
          <CelebrationsTab
            config={formState.celebrationConfig}
            onUpdate={(config) => updateFormField('celebrationConfig', config)}
          />
        )}
        {activeTab === 'animations' && (
          <AnimationsTab
            config={formState.animationConfig}
            onUpdate={(config) => updateFormField('animationConfig', config)}
          />
        )}
        {activeTab === 'betting' && (
          <BettingTab
            config={formState.betConfig}
            onUpdate={(config) => updateFormField('betConfig', config)}
          />
        )}
        {activeTab === 'game-options' && (
          <GameOptionsTab
            config={formState.gameOptions}
            onUpdate={(config) => updateFormField('gameOptions', config)}
          />
        )}
      </div>
    </div>
  );
}
